import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import GreenCanvas from '../../assets/images/GreenCanvas.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';


function AdminPartners() {
    const { pathname } = useLocation();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container'>

            <div >
                <Breadcrumb route="/case-studies" pageTitle="Admin Partners" />
            </div>
            <section className='article-row-1' id='admin-partners-background'>
                <p> Retirement/Financial Services</p>
                <h2>Admin Partners</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: Cherry Hill, New Jersey 🇺🇲</span>
                    </div>
                </div>
            </section>

            <section className='article-intro-section'>
                <h1>How We Upgraded A Client's Core System and Boosted Their Operational Efficiency</h1>
                <p>
                    Admin Partners is a full-service Third-Party Administrator (TPA) specializing in retirement plan administration for tax-exempt and government organizations. They service plans for public education institutions, local and state governments, non-profits, and religious organizations.

                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>Admin Partners had to migrate their system from Relius to its latest v2017 version, risking losing access to support and system functionality had they not upgraded and integrated in time with their mission-critical system. The legacy connection method, through socket, had to be overhauled to use Web Services for seamless data retrieval.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We migrated from Relius to Microsoft SQL Server while preserving data integrity. We set up QA and Production environments, conducted rigorous integration testing, and modified bCompliant for Web Services compatibility with Relius Connect. We also took care of user acceptance testing and put together comprehensive documentation to align with Admin Partners' expectations and operational needs.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>We delivered a seamless Relius upgrade, integrated bCompliant with Relius Connect for efficient data interchange. Furthermore, we established backup schedules for synchronization and compliance. As a result, Admin Partners benefit from uninterrupted functionality across trade exports, file creation, and disbursement processes. We minimized disruptions and exceeded client expectations.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>Our Client's Situation:</h3>
                <p>
                    Our client had to migrate from their cornerstone system, Relius, to its most advanced v2017 version to sustain operational efficiency. At the same time, the transition presented some complexities.
                    Beyond the technical upgrade, our client had to integrate the mission critical system, bCompliant, with Relius Connect. For that to happen, bCompliant required a crucial modification that would enable the efficient retrieval of data from the updated Relius database.
                </p>
                <p>
                    The application, which was originally designed to connect directly to the database directly through sockets, was modified to connect through Web Services, instead.
                </p>
                <p>
                    Our client had limited resources, which made them unable to:
                </p>
                <ul>
                    <li>Upgrade their foundational system to the latest release</li>
                    <li>Enable an efficient retrieval of data</li>
                    <li>Modify their mission-critical system</li>
                </ul>
                <p>
                    Hadn't they promptly found a solution, they would have faced the end of life for the Relius product, they would have been unable to use the new version, and they would have lost access to support, too.
                </p>
            </section>


            <section className='article-section-3'>
                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    Firstly, we extracted and migrated Compliant-specific tables from the Relius database to Microsoft SQL Server to ensure data integrity and compatibility. We then defined and created distinct QA and Production environments with configurations mirroring real-world scenarios.
                </p>
                <p>
                    We proceeded by conducting integration testing to identify those points that required Relius Connect. We thoroughly documented database calls and integration points between Relius (Oracle) and bCompliant (SQL Server), too, with the purpose of facilitating clear understanding and future maintenance.
                </p>
                <p>
                    Finally, we modified the bCompliant system to seamlessly incorporate Relius Connect functionality for a number of tasks, and engaged in user acceptance testing (UAT) to ensure alignment of the upgraded system and integrated Relius Connect features with our client's expectations.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>The migration of compliant-specific tables and establishment of environments.</li>
                    <li>Integration testing and Relius Connect integration.</li>
                    <li>Documentation and database call clarification.</li>
                    <li>The adaptation of bCompliant for Relius Connect.</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>We've seamlessly migrated our client's bCompliant-specific data tables from Relius to Microsoft SQL Server, integrated bCompliant with Relius Connect to facilitate fluid data interchange, established dedicated QA and Production environments mirroring real-world scenarios, performed integration testing for import processes, and validated data flow between systems.</li>
                    <li>We've also clearly documented integration guidelines, communicated changes to our client's stakeholders, modified bCompliant for Relius Connect utilization, engaged in thorough user acceptance testing, and incorporated user feedback for system refinement.</li>
                    <li>We've leveraged our expertise in data backup, security, and regulatory compliance to establish an SQL Server backup schedule that would ensure synchronization, address data integrity challenges arising from separate databases, guarantee compliance with industry regulations, and manage potential risks.</li>
                    <li>We worked closely with Admin Partners, upholding service excellence during the upgrade process and ensuring minimal disruption to client operations all while meeting or exceeding expectations.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    The meticulous execution of our approach led to a seamless Relius upgrade, efficient bCompliant integration, and streamlined data interchange through Relius Connect.
                    Our client achieved uninterrupted functionality for trade export, MATC file creation, and standard detail disbursement. It just goes to show that strategic partnerships and forward-thinking technology solutions go a long way when overcoming complex challenges within the financial services domain.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>

            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>



            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default AdminPartners;